import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Column } from 'react-rainbow-components';
import Button from 'components/Button';
import Plus from 'components/icons/plus';
import Table from 'components/Table';
import useManageCustomerFallbackNumbers from 'data/hooks/useManageCustomerFallbackNumbers';
import { Phonenumber } from 'data/firestore/agent/customer/phonenumber/types';
import useHttpQuery from 'data/firestore/useHttpQuery';
import { CREATE_EDIT_CUSTOMER_PHONE_NUMBER_DRAWER } from '../../../../constants';
import CreateEditCustomerPhoneNumberDrawer from './CreateEditCustomerPhoneNumberDrawer';
import ActionsColumn from './columns/actions';
import {
    Container, TableContainer, Header, Title, Description,
    HeaderContent,
} from './styled';
import PhoneColumn from './columns/phoneNumber';
import PhoneTypeColumn from './columns/phoneType';
import VerifyPhoneNumber from './columns/verify';

const defaultPhoneTypes = [
    'mobile', 'home', 'work', 'spouse', 'relative', 'other', 'unknown',
];

const CustomerDetailsPhoneNumbers = () => {
    const { agentId = '', customerId = '' } = useParams();
    const { create } = useManageCustomerFallbackNumbers({ agentId, customerId });

    const {
        data: phoneNumbers = [],
        isLoading: isLoadingPhoneNumbers,
    } = useHttpQuery<Phonenumber[]>({
        key: ['customer-phone-numbers', customerId],
        pathname: `/agents/${agentId}/customers/${customerId}/phone-numbers`,
    });

    const sortedData = useMemo(() => [...phoneNumbers].sort(
        (a) => (a.type === 'primary' ? -1 : 1),
    ), [phoneNumbers]);

    const phoneTypes = useMemo(() => Array.from(new Set([
        ...defaultPhoneTypes,
        ...phoneNumbers.map((phone) => phone.phoneType.trim()),
    ])), [phoneNumbers]);

    return (
        <Container>
            <Header>
                <HeaderContent>
                    <Title>Customer Phone Numbers</Title>
                    <Description>
                        These are the customer fallback phone numbers.
                    </Description>
                </HeaderContent>
                <Button
                    variant="outline-brand"
                    size="medium"
                    borderRadius="semi-rounded"
                    onClick={create}
                >
                    <Plus />
                    Add Number
                </Button>
            </Header>
            <TableContainer>
                <Table
                    data={sortedData}
                    keyField="id"
                    isLoading={isLoadingPhoneNumbers}
                >
                    <Column
                        header="Phone number"
                        field="phoneNumber"
                        component={PhoneColumn}
                    />
                    <Column
                        header="Phone Type"
                        field="phoneType"
                        component={PhoneTypeColumn}
                        defaultWidth={200}
                    />
                    <Column
                        header="Verified"
                        field="verified"
                        component={VerifyPhoneNumber}
                        defaultWidth={120}
                    />
                    <Column
                        header="Actions"
                        component={ActionsColumn}
                        defaultWidth={240}
                    />
                </Table>
            </TableContainer>
            <CreateEditCustomerPhoneNumberDrawer
                modalId={CREATE_EDIT_CUSTOMER_PHONE_NUMBER_DRAWER}
                phoneTypes={phoneTypes}
            />
        </Container>
    );
};

export default CustomerDetailsPhoneNumbers;
